import React, { useState } from 'react';
import { CheckCircle2Icon, AlertTriangleIcon, SparklesIcon, UsersIcon, MessageSquareWarningIcon, ChevronDownIcon, ChevronUpIcon, RefreshCwIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
interface WorkerResult {
  name: string;
  role: string;
  score: number;
  total: number;
  status: 'good' | 'partial' | 'poor';
  missedQuestion?: string;
}
function buildCrewResults(crewRoles: {
  name: string;
  role: string;
}[]): WorkerResult[] {
  return crewRoles.map((cr, i) => {
    // Simulate varied quiz results — most pass, last 1-2 have partial scores
    const isPartial = i >= crewRoles.length - 2 && crewRoles.length > 2;
    if (isPartial && i === crewRoles.length - 2) {
      return {
        name: cr.name,
        role: cr.role,
        score: 3,
        total: 5,
        status: 'partial' as const,
        missedQuestion: 'Missed questions on hazard controls and emergency procedures'
      };
    }
    if (isPartial && i === crewRoles.length - 1) {
      return {
        name: cr.name,
        role: cr.role,
        score: 4,
        total: 5,
        status: 'partial' as const,
        missedQuestion: 'Missed question on step-specific safety requirements'
      };
    }
    return {
      name: cr.name,
      role: cr.role,
      score: 5,
      total: 5,
      status: 'good' as const
    };
  });
}
const statusConfig = {
  good: {
    label: 'Good',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-600',
    icon: CheckCircle2Icon,
    iconColor: 'text-green-500',
    dot: 'bg-green-500'
  },
  partial: {
    label: 'Partial',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-600',
    icon: AlertTriangleIcon,
    iconColor: 'text-amber-500',
    dot: 'bg-amber-500'
  },
  poor: {
    label: 'Poor',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-500',
    icon: AlertTriangleIcon,
    iconColor: 'text-red-500',
    dot: 'bg-red-500'
  }
};
export function Screen18_CrewQuizResults({
  data,
  onBack,
  onNext
}: ScreenProps) {
  const CREW_RESULTS = buildCrewResults(data.crewRoles);
  const [expandedCard, setExpandedCard] = useState<string | null>(CREW_RESULTS.find(r => r.status === 'partial')?.name || null);
  const goodCount = CREW_RESULTS.filter(r => r.status === 'good').length;
  const totalCount = CREW_RESULTS.length;
  return <div className="flex flex-col h-full" data-id="element-1262">
      <div className="mb-5" data-id="element-1263">
        <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-1264">
          Crew Understanding Status
        </h2>
        <p className="text-gray-500 text-sm" data-id="element-1265">
          Quiz results for the Pre-Task Plan safety briefing.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-5" data-id="element-1266">
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1267">
          <div className="grid grid-cols-2 gap-3" data-id="element-1268">
            <div data-id="element-1269">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5" data-id="element-1270">
                Project
              </p>
              <p className="text-gray-900 font-medium text-sm" data-id="element-1271">
                {data.project || 'Project'}
              </p>
            </div>
            <div data-id="element-1272">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5" data-id="element-1273">
                Location
              </p>
              <p className="text-gray-900 font-medium text-sm" data-id="element-1274">
                {data.location || 'Location'}
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-1275">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5" data-id="element-1276">
              Task
            </p>
            <p className="text-yellow-500 font-bold text-sm" data-id="element-1277">
              {data.taskTitle || 'Task'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3" data-id="element-1278">
          <UsersIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-1279" />
          <div className="flex-1" data-id="element-1280">
            <div className="flex items-center justify-between mb-1.5" data-id="element-1281">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wider" data-id="element-1282">
                Crew Readiness
              </span>
              <span className="text-xs font-bold text-gray-900" data-id="element-1283">
                {goodCount}/{totalCount} Good
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 flex overflow-hidden gap-0.5" data-id="element-1284">
              {CREW_RESULTS.map((r, i) => <div key={i} className={`h-2 flex-1 rounded-full ${statusConfig[r.status].dot}`} data-id="element-1285" />)}
            </div>
          </div>
        </div>

        <section data-id="element-1286">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-1287">
            Crew Quiz Results
          </h3>
          <div className="space-y-2.5" data-id="element-1288">
            {CREW_RESULTS.map(worker => {
            const config = statusConfig[worker.status];
            const StatusIcon = config.icon;
            const isExpanded = expandedCard === worker.name;
            const hasDetails = !!worker.missedQuestion;
            return <motion.div key={worker.name} layout className={`${config.bg} border ${config.border} rounded-xl overflow-hidden`} data-id="element-1289">
                  <button onClick={() => hasDetails && setExpandedCard(isExpanded ? null : worker.name)} className="w-full px-4 py-3 flex items-center gap-3 text-left" data-id="element-1290">
                    <StatusIcon className={`w-5 h-5 ${config.iconColor} flex-shrink-0`} data-id="element-1291" />
                    <div className="flex-1 min-w-0" data-id="element-1292">
                      <div className="flex items-center gap-2" data-id="element-1293">
                        <span className="text-gray-900 font-semibold text-sm" data-id="element-1294">
                          {worker.name}
                        </span>
                        <span className="text-gray-400 text-xs" data-id="element-1295">·</span>
                        <span className="text-gray-500 text-xs" data-id="element-1296">
                          {worker.role}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5" data-id="element-1297">
                        <span className="text-gray-700 text-xs font-medium" data-id="element-1298">
                          {worker.score} / {worker.total}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${config.text}`} data-id="element-1299">
                          {config.label}
                        </span>
                      </div>
                    </div>
                    {hasDetails && <div className="text-gray-400 flex-shrink-0" data-id="element-1300">
                        {isExpanded ? <ChevronUpIcon className="w-4 h-4" data-id="element-1301" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-1302" />}
                      </div>}
                  </button>
                  <AnimatePresence data-id="element-1303">
                    {isExpanded && hasDetails && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25,
                  ease: 'easeInOut'
                }} className="overflow-hidden" data-id="element-1304">
                        <div className="px-4 pb-3 space-y-2.5 border-t border-gray-200 pt-2.5" data-id="element-1305">
                          {worker.missedQuestion && <div data-id="element-1306">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5" data-id="element-1307">
                                Missed Question
                              </p>
                              <p className="text-amber-700 text-sm" data-id="element-1308">
                                {worker.missedQuestion}
                              </p>
                            </div>}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </section>

        <section data-id="element-1309">
          <div className="flex items-center gap-2 mb-3" data-id="element-1310">
            <SparklesIcon className="w-5 h-5 text-yellow-500" data-id="element-1311" />
            <h3 className="text-xs font-bold text-yellow-600 uppercase tracking-wider" data-id="element-1312">
              AI Crew Readiness Analysis
            </h3>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-3" data-id="element-1313">
            <p className="text-yellow-700 text-sm leading-relaxed" data-id="element-1314">
              {goodCount} of {totalCount} crew members scored perfectly.
              {CREW_RESULTS.filter(r => r.status === 'partial').map(r => ` ${r.name} missed questions on safety procedures.`).join('')}
            </p>
            <div className="bg-gray-50 rounded-lg p-3" data-id="element-1315">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1" data-id="element-1316">
                Suggested Actions
              </p>
              {CREW_RESULTS.filter(r => r.status === 'partial').map((r, i) => <p key={i} className="text-gray-900 text-sm font-medium mt-1" data-id="element-1317">
                    {i + 1}. Review missed safety topics with {r.name} before
                    work begins.
                  </p>)}
            </div>
            <div className="flex items-center gap-2 pt-1" data-id="element-1318">
              <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-1319" />
              <span className="text-amber-600 text-xs font-bold uppercase tracking-wider" data-id="element-1320">
                Ready with minor review
              </span>
            </div>
          </div>
        </section>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-2.5 bg-gray-50" data-id="element-1321">
        <PrimaryButton onClick={onNext} icon={<CheckCircle2Icon className="w-5 h-5" data-id="element-1323" />} data-id="element-1322">
          Finalize PTP
        </PrimaryButton>
        <SecondaryButton onClick={onBack} icon={<MessageSquareWarningIcon className="w-5 h-5" data-id="element-1325" />} data-id="element-1324">
          Review Updated PTP
        </SecondaryButton>
        <SecondaryButton onClick={() => {}} icon={<RefreshCwIcon className="w-5 h-5" data-id="element-1327" />} data-id="element-1326">
          Repeat Safety Quiz
        </SecondaryButton>
      </div>
    </div>;
}